import { leaveTypeDisplayName } from './leave-balance-summary';
import {
  CONSERVATIVE_STATIC_OFFERED_LEAVE_SUBMIT_TOKENS,
  LEAVE_SUBMIT_TOKEN_OPERATIONAL_KEYS,
  leaveSubmitTypeHoldExplanation,
  type LeaveSubmitToken,
  type LeaveTypePolicyReadiness,
} from './leave-type-policy-readiness';

export type SelfServiceLeaveTypeRow = {
  readonly leaveType: string;
  readonly label: string;
  readonly ready: boolean;
  readonly selfService: boolean;
  readonly holdReason: string | null;
  readonly evidenceRequiredAfterDays: number | null;
};

export type SelfServiceLeaveTypeList = {
  readonly registrySchemaVersion: string | null;
  readonly atDate: string;
  readonly types: readonly SelfServiceLeaveTypeRow[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function parseSelfServiceLeaveTypeList(input: unknown): SelfServiceLeaveTypeList | null {
  if (!isRecord(input)) return null;
  const atDate = input.at_date;
  const registry = input.registry_schema_version;
  const rawTypes = input.types;
  if (typeof atDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(atDate)) return null;
  if (registry !== null && typeof registry !== 'string') return null;
  if (!Array.isArray(rawTypes)) return null;

  const types: SelfServiceLeaveTypeRow[] = [];
  for (const entry of rawTypes) {
    if (!isRecord(entry)) return null;
    const leaveType = entry.leave_type;
    const label = entry.label;
    const ready = entry.ready;
    const selfService = entry.self_service;
    const holdReason = entry.hold_reason;
    const evidenceRequiredAfterDays = entry.evidence_required_after_days;
    if (typeof leaveType !== 'string' || leaveType.length === 0) return null;
    if (typeof label !== 'string' || label.length === 0) return null;
    if (typeof ready !== 'boolean' || typeof selfService !== 'boolean') return null;
    if (holdReason !== null && typeof holdReason !== 'string') return null;
    if (
      evidenceRequiredAfterDays !== null &&
      (typeof evidenceRequiredAfterDays !== 'number' ||
        !Number.isInteger(evidenceRequiredAfterDays))
    ) {
      return null;
    }
    types.push({
      leaveType,
      label,
      ready,
      selfService,
      holdReason: holdReason as string | null,
      evidenceRequiredAfterDays:
        evidenceRequiredAfterDays === null ? null : (evidenceRequiredAfterDays as number),
    });
  }

  return {
    registrySchemaVersion: registry as string | null,
    atDate,
    types,
  };
}

/**
 * `list_self_service_leave_types_server_controlled` emits a closed set of hold
 * reasons, and only ONE of them means the type is not configured:
 *
 * - `COORDINATOR_ONLY`                    — configured; a coordinator requests it
 * - `POLICY_ABSENT`                       — genuinely not set up
 * - `TENANT_POLICY_REQUESTABILITY_NEVER`  — configured; the provider does not accept requests
 * - `TENANT_POLICY_OR_REGISTRY_HELD`      — configured; paused
 * - anything else                         — a registry `held_reason` token; configured, paused
 *
 * Previously every reason but `COORDINATOR_ONLY` fell through to "not set up
 * for your organisation yet — ask your coordinator". For a configured-but-held
 * type that sends the worker to a coordinator who then finds nothing to set up.
 * Compassionate leave on a tenant that has the policy but holds it for payroll
 * sync is exactly this shape.
 *
 * A registry `held_reason` is an internal token
 * (e.g. `HELD_COMPASSIONATE_PAYROLL_AND_SYNC_UNPROVEN`) and is never rendered:
 * anything unrecognised takes the generic held wording.
 */
export function selfServiceLeaveTypeHelperText(
  row: SelfServiceLeaveTypeRow,
  readiness: LeaveTypePolicyReadiness | null,
): string | null {
  if (row.ready && row.selfService) return null;
  if (row.holdReason === 'READINESS_UNAVAILABLE') {
    return 'Leave policy availability could not be checked. Retry before submitting.';
  }
  if (row.holdReason === 'POLICY_ABSENT') {
    return 'Your organisation needs to configure and approve this leave policy. Contact your coordinator or tenant administrator.';
  }
  if (row.holdReason === 'COORDINATOR_ONLY') {
    return 'This leave type must be requested by a coordinator.';
  }
  if (row.holdReason === 'TENANT_POLICY_REQUESTABILITY_NEVER') {
    return 'Your organisation does not accept requests for this leave type.';
  }
  if (row.holdReason !== null && row.holdReason !== 'POLICY_ABSENT') {
    return 'This leave type is set up but on hold, so requests are paused — ask your coordinator.';
  }
  return leaveSubmitTypeHoldExplanation(row.leaveType, readiness);
}

export function isSelfServiceLeaveTypeSelectable(row: SelfServiceLeaveTypeRow): boolean {
  return row.ready && row.selfService;
}

function selfServiceFallbackLabel(token: string): string {
  try {
    return leaveTypeDisplayName(token);
  } catch {
    return token.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/**
 * When the live self-service RPC cannot be fetched, list every submit token
 * but enable none without policy evidence. Keep the choices visible with an
 * unavailable reason; a failed read is not an absent or configured policy.
 */
export function conservativeSelfServiceLeaveTypeFallback(atDate: string): SelfServiceLeaveTypeList {
  const conservative = new Set<string>(CONSERVATIVE_STATIC_OFFERED_LEAVE_SUBMIT_TOKENS);
  const types: SelfServiceLeaveTypeRow[] = (
    Object.keys(LEAVE_SUBMIT_TOKEN_OPERATIONAL_KEYS) as LeaveSubmitToken[]
  ).map((leaveType) => {
    const offered = conservative.has(leaveType);
    return {
      leaveType,
      label: selfServiceFallbackLabel(leaveType),
      ready: offered,
      selfService: offered,
      holdReason: offered ? null : 'READINESS_UNAVAILABLE',
      evidenceRequiredAfterDays: null,
    };
  });
  return {
    registrySchemaVersion: null,
    atDate,
    types,
  };
}
